"use client";

import {
  LabelText,
  RadioButton,
  SelectField,
} from "@fanduel/formation-react-components";
import cx from "classnames";
import { useState } from "react";
import {
  HEAD_TO_HEAD_BETS_FILTER_APPLIED,
  HEAD_TO_HEAD_STATUS_MAP,
} from "@/constants/amplitude";
import { SCREENS } from "@/constants/breakpoints";
import { GetHeadToHeadPlayerPropsQuery } from "@/graphql/gql/graphql";
import {
  getUniquePlayerNumberfireIds,
  groupByPlayerNumberfireId,
} from "@/utils/headToHead";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import useMedia from "@/utils/hooks/useMedia";
import { EmptyMessage } from "../../PlayerStats/PlayerProps/oddsUnavailable/OddsUnavailable";

import { PlayerPropsOddsQuery } from "../../types";
import PlayerProp from "./PlayerProp";
import styles from "./featuredPlayerProps.module.scss";

type PlayerPropsOdds = NonNullable<PlayerPropsOddsQuery["playerPropOdds"]>;
type OddsKeys = Exclude<keyof PlayerPropsOdds, "__typename">;

enum PropBetSelection {
  PASSING = "Passing",
  RECEIVING = "Receiving",
  RUSHING = "Rushing",
  PASSING_TDS = "PassingTds",
}

const SELECTION_TO_ODDS_KEY: Record<PropBetSelection, OddsKeys> = {
  [PropBetSelection.PASSING]: "totalPassingYards",
  [PropBetSelection.RECEIVING]: "totalReceivingYards",
  [PropBetSelection.RUSHING]: "totalRushingYards",
  [PropBetSelection.PASSING_TDS]: "totalPassingTDs",
};

const SELECTION_OPTIONS = [
  { label: "Passing Yards", value: PropBetSelection.PASSING },
  { label: "Receiving Yards", value: PropBetSelection.RECEIVING },
  { label: "Rushing Yards", value: PropBetSelection.RUSHING },
  { label: "Passing Touchdowns", value: PropBetSelection.PASSING_TDS },
];

export default function FeaturedPlayerPropsClient({
  data,
}: {
  data: GetHeadToHeadPlayerPropsQuery;
}) {
  const { logAmplitudeEvent } = useAmplitude();
  const { eventName, gameState } = useHeadToHeadContext();
  const ampProps = {
    Module: "featured_player_props",
    "Event Name": eventName,
    "Page State": HEAD_TO_HEAD_STATUS_MAP[gameState],
    "Filter Name": "featured_player_props",
  };

  const [radio, setRadio] = useState(PropBetSelection.PASSING);

  const isMobile = useMedia(`(max-width: ${SCREENS.SM})`);

  const buildOnChange = (id: PropBetSelection) => () => {
    setRadio(id);
    logAmplitudeEvent(HEAD_TO_HEAD_BETS_FILTER_APPLIED, {
      ...ampProps,
      "Filter Value": id,
    });
  };
  const isSelected = (id: PropBetSelection) => id === radio;

  const odds = data.event?.playerPropOdds?.[SELECTION_TO_ODDS_KEY[radio]] ?? [];

  const playerIds = getUniquePlayerNumberfireIds(odds).slice(0, 4);
  const groupings = groupByPlayerNumberfireId(odds);
  const hasProps = playerIds.length > 0;

  const handleValueChange = (value: string) => {
    setRadio(value as PropBetSelection);
    logAmplitudeEvent(HEAD_TO_HEAD_BETS_FILTER_APPLIED, {
      ...ampProps,
      "Filter Value": value,
    });
  };

  return (
    <>
      {hasProps ? null : <EmptyMessage className={styles.emptyMessage} />}

      <div className={styles.content}>
        {isMobile ? (
          <SelectField
            id="props-selection"
            label="Type"
            css={{ flex: 1, marginBottom: 16 }}
            onValueChange={handleValueChange}
            options={SELECTION_OPTIONS}
          />
        ) : (
          <>
            <LabelText
              as="label"
              id="props-selection-label"
              className={styles.radioLabel}
            >
              Type
            </LabelText>
            <fieldset
              className={styles.radioBtns}
              aria-labelledby="props-selection-label"
            >
              <RadioButton
                id={PropBetSelection.PASSING}
                onChange={buildOnChange(PropBetSelection.PASSING)}
                selected={isSelected(PropBetSelection.PASSING)}
                label="Passing Yards"
              />
              <RadioButton
                id={PropBetSelection.RECEIVING}
                onChange={buildOnChange(PropBetSelection.RECEIVING)}
                selected={isSelected(PropBetSelection.RECEIVING)}
                label="Receiving Yards"
              />
              <RadioButton
                id={PropBetSelection.RUSHING}
                onChange={buildOnChange(PropBetSelection.RUSHING)}
                selected={isSelected(PropBetSelection.RUSHING)}
                label="Rushing Yards"
              />
              <RadioButton
                id={PropBetSelection.PASSING_TDS}
                onChange={buildOnChange(PropBetSelection.PASSING_TDS)}
                selected={isSelected(PropBetSelection.PASSING_TDS)}
                label="Passing Touchdowns"
              />
            </fieldset>
          </>
        )}

        <div
          className={cx(styles.propsList, !hasProps && styles.oddsUnavailable)}
        >
          {hasProps
            ? null
            : [null, null, null, null].map((_, index) => (
                <PlayerProp
                  key={index.toString()}
                  overSelection={null}
                  underSelection={null}
                  isMobile={isMobile}
                />
              ))}
          {playerIds.map((playerId) => {
            const [over, under] = groupings[playerId] ?? [];

            if (!over || !under) {
              return null;
            }

            return (
              <PlayerProp
                key={playerId}
                overSelection={over}
                underSelection={under}
                isMobile={isMobile}
              />
            );
          })}
        </div>
      </div>
    </>
  );
}
